import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Card, Row, Col } from "react-bootstrap";
import moment from "moment";

import { DataTable } from "../../../common";
import { ButtonView, Select, Input } from "../../../controls";

const OccupancyHistoryTab = ({
  roomList,
  occupancyHistoryList,
  occupancyHistoryPagination,
  getOccupancyHistory,
  occupancyHistoryPending,
  locationList,
  occupancyHistoryPage,
  setOccupancyHistoryPage,
  occupancyHistoryFilters,
  setOccupancyHistoryFilters,
}) => {
  const [filters, setFilters] = useState({
    roomId: (occupancyHistoryFilters && occupancyHistoryFilters.roomId) || "",
    location: (occupancyHistoryFilters && occupancyHistoryFilters.location) || "",
    fromDate: (occupancyHistoryFilters && occupancyHistoryFilters.fromDate) ? new Date(occupancyHistoryFilters.fromDate) : null,
    toDate: (occupancyHistoryFilters && occupancyHistoryFilters.toDate) ? new Date(occupancyHistoryFilters.toDate) : null,
  });
  const [pagination, setPagination] = useState({ page: occupancyHistoryPage || 1, limit: 10 });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Load general history by default on mount using the preserved page and filters
    const payload = {
      page: occupancyHistoryPage || 1,
      limit: 10,
    };
    if (occupancyHistoryFilters) {
      if (occupancyHistoryFilters.roomId) payload.roomId = occupancyHistoryFilters.roomId;
      if (occupancyHistoryFilters.location) payload.location = occupancyHistoryFilters.location;
      if (occupancyHistoryFilters.fromDate) {
        payload.fromDate = moment(occupancyHistoryFilters.fromDate).startOf("day").toISOString();
      }
      if (occupancyHistoryFilters.toDate) {
        payload.toDate = moment(occupancyHistoryFilters.toDate).endOf("day").toISOString();
      }
    }
    getOccupancyHistory(payload);
  }, [getOccupancyHistory, occupancyHistoryPage, occupancyHistoryFilters]);

  useEffect(() => {
    if (occupancyHistoryPage) {
      setPagination((prev) => ({ ...prev, page: occupancyHistoryPage }));
    }
  }, [occupancyHistoryPage]);

  const roomOptions = roomList.map((room) => ({
    value: room._id,
    label: `${room.name} (${room.location && typeof room.location === "object" ? (room.location.name || "") : (room.location || "")})`,
  }));

  const locationOptions = Array.from(
    new Set((locationList || []).map((loc) => loc.name).filter(Boolean))
  ).map((name) => ({
    value: name,
    label: name,
  }));

  const selectedLocation = locationOptions.find((o) => o.value === filters.location) || null;

  const handleSelectChange = (selectedOption) => {
    setFilters((prev) => ({
      ...prev,
      roomId: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleSearch = (overridePage, overrideLimit) => {
    const pageNum = (typeof overridePage === "number" || typeof overridePage === "string") ? overridePage : undefined;
    const limitNum = (typeof overrideLimit === "number" || typeof overrideLimit === "string") ? overrideLimit : undefined;

    const validationErrors = {};
    if (filters.fromDate && filters.toDate) {
      if (moment(filters.fromDate).isAfter(moment(filters.toDate))) {
        validationErrors.fromDate = "From Date cannot be greater than To Date.";
        validationErrors.toDate = "To Date cannot be less than From Date.";
      }
    }

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    if (setOccupancyHistoryFilters) {
      setOccupancyHistoryFilters({
        roomId: filters.roomId,
        location: filters.location,
        fromDate: filters.fromDate ? filters.fromDate.toISOString() : null,
        toDate: filters.toDate ? filters.toDate.toISOString() : null,
      });
    }

    const payload = { 
      page: pageNum || pagination.page, 
      limit: limitNum || pagination.limit 
    };
    if (filters.roomId) payload.roomId = filters.roomId;
    if (filters.location) payload.location = filters.location;
    if (filters.fromDate) payload.fromDate = moment(filters.fromDate).startOf("day").toISOString();
    if (filters.toDate) payload.toDate = moment(filters.toDate).endOf("day").toISOString();

    getOccupancyHistory(payload);
  };

  const handleReset = () => {
    const cleared = {
      roomId: "",
      location: "",
      fromDate: null,
      toDate: null,
    };
    setFilters(cleared);
    setErrors({});
    if (setOccupancyHistoryPage) {
      setOccupancyHistoryPage(1);
    }
    if (setOccupancyHistoryFilters) {
      setOccupancyHistoryFilters(cleared);
    }
    setPagination({ page: 1, limit: 10 });
    getOccupancyHistory({ page: 1, limit: 10 });
  };

  const columns = [
    {
      name: "Room Name",
      selector: "roomId.name",
      sortable: true,
      grow: 3,
      cell: (row) => row.roomId ? row.roomId.name : "N/A",
    },
    {
      name: "Location",
      selector: "roomId.location",
      sortable: true,
      grow: 2,
      sortFunction: (rowA, rowB) => {
        const locA = rowA.roomId ? (typeof rowA.roomId.location === "object" ? (rowA.roomId.location.name || "") : (rowA.roomId.location || "")) : "";
        const locB = rowB.roomId ? (typeof rowB.roomId.location === "object" ? (rowB.roomId.location.name || "") : (rowB.roomId.location || "")) : "";
        return locA.localeCompare(locB);
      },
      cell: (row) => row.roomId ? (row.roomId.location && typeof row.roomId.location === "object" ? (row.roomId.location.name || "N/A") : (row.roomId.location || "N/A")) : "N/A",
    },
    {
      name: "Booked For",
      selector: "employeeId.firstName",
      sortable: true,
      grow: 3,
      cell: (row) =>
        row.employeeId
          ? `${row.employeeId.firstName} ${row.employeeId.lastName}`
          : "N/A",
    },
    {
      name: "Purpose",
      selector: "purpose",
      sortable: true,
      grow: 3,
      cell: (row) => (
        <span
          title={row.purpose || ""}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block",
            maxWidth: "100%"
          }}
        >
          {row.purpose || "-"}
        </span>
      ),
    },
    {
      name: "Status",
      selector: "status",
      sortable: true,
      grow: 2,
      cell: (row) => {
        let variant = "secondary-badge";
        let text = "N/A";

        if (row.status) {
          const lStatus = row.status.toLowerCase();
          text = row.status.charAt(0).toUpperCase() + lStatus.slice(1);
          if (lStatus === 'active') variant = "success-badge";
          else if (lStatus === 'upcoming') variant = "warning-badge";
          else if (lStatus === 'completed') variant = "completed-badge";
        } else {
          // Fallback before API returns status
          const checkOutMoment = row.checkOutTime ? moment(row.checkOutTime) : moment(row.checkInTime).set({ hour: 21, minute: 0, second: 0 });
          if (row.isCancelled) {
            variant = "secondary-badge";
            text = "Cancelled";
          } else if (checkOutMoment.isBefore(moment())) {
            variant = "completed-badge";
            text = "Completed";
          } else if (moment(row.checkInTime).isAfter(moment())) {
            variant = "warning-badge";
            text = "Upcoming";
          } else {
            variant = "success-badge";
            text = "Active";
          }
        }

        return (
          <span className={`status-dot-badge ${variant}`}>
            <span className="dot"></span>
            {text}
          </span>
        );
      },
    },
    {
      name: "Schedule",
      selector: "checkInTime",
      sortable: true,
      grow: 4,
      cell: (row) => {
        const dateStr = moment(row.checkInTime).format("DD MMM YYYY");
        const startStr = moment(row.checkInTime).format("hh:mm A");
        const endStr = row.checkOutTime ? moment(row.checkOutTime).format("hh:mm A") : "09:00 PM";
        return `${dateStr}, ${startStr} - ${endStr}`;
      },
    },
  ];

  const selectedRoom = roomOptions.find((o) => o.value === filters.roomId) || null;

  return (
    <div className="occupancy-history-tab mt-3">
      <Card className="mb-4">
        <Card.Body>
          <Card.Header>
            <Card.Title className="m-0">Search Filters</Card.Title>
          </Card.Header>
          <div className="p-3">
            <Row>
              <Col md={3} className="mb-3">
                <Select
                  label="Room"
                  value={selectedRoom}
                  options={roomOptions}
                  onChange={handleSelectChange}
                  placeholder="Select Room"
                />
              </Col>
              <Col md={3} className="mb-3">
                <Select
                  label="Location"
                  value={selectedLocation}
                  options={locationOptions}
                  onChange={(selectedOption) =>
                    setFilters({ ...filters, location: selectedOption ? selectedOption.value : "" })
                  }
                  placeholder="Select Location"
                />
              </Col>
              <Col md={3} className="mb-3">
                <Input
                  type="date"
                  name="fromDate"
                  label="From Date"
                  value={filters.fromDate ? moment(filters.fromDate).format("YYYY-MM-DD") : ""}
                  onChange={(e) => {
                    const d = e.target.value ? new Date(e.target.value) : null;
                    setFilters({ ...filters, fromDate: d });
                    setErrors((prev) => ({ ...prev, fromDate: "", toDate: "" }));
                  }}
                  error={errors.fromDate}
                />
              </Col>
              <Col md={3} className="mb-4 mb-md-3 mt-3 mt-md-0">
                <Input
                  type="date"
                  name="toDate"
                  label="To Date"
                  value={filters.toDate ? moment(filters.toDate).format("YYYY-MM-DD") : ""}
                  onChange={(e) => {
                    const d = e.target.value ? new Date(e.target.value) : null;
                    setFilters({ ...filters, toDate: d });
                    setErrors((prev) => ({ ...prev, fromDate: "", toDate: "" }));
                  }}
                  error={errors.toDate}
                />
              </Col>
              <Col xs={12} className="d-flex justify-content-end mt-2">
                <div className="me-2">
                  <ButtonView
                    onClick={handleReset}
                    title="Reset"
                    styles="primal"
                  />
                </div>
                <ButtonView
                  onClick={() => handleSearch()}
                  title={occupancyHistoryPending ? "Searching..." : "Search"}
                  styles="secondary"
                  disabled={occupancyHistoryPending}
                />
              </Col>
            </Row>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <Card.Header>
            <Card.Title className="m-0">Occupancy History Logs</Card.Title>
          </Card.Header>
          <DataTable
            data={occupancyHistoryList || []}
            columns={columns}
            pagination={true}
            paginationServer={true}
            paginationTotalRows={occupancyHistoryPagination ? occupancyHistoryPagination.totalDocs : 0}
            paginationDefaultPage={pagination.page}
            onChangePage={(page) => {
              setPagination({ ...pagination, page });
              if (setOccupancyHistoryPage) {
                setOccupancyHistoryPage(page);
              }
              handleSearch(page, pagination.limit);
            }}
            onChangeRowsPerPage={(limit, page) => {
              setPagination({ page, limit });
              if (setOccupancyHistoryPage) {
                setOccupancyHistoryPage(page);
              }
              handleSearch(page, limit);
            }}
          />
        </Card.Body>
      </Card>
    </div>
  );
};

OccupancyHistoryTab.propTypes = {
  roomList: PropTypes.array.isRequired,
  occupancyHistoryList: PropTypes.array.isRequired,
  occupancyHistoryPagination: PropTypes.object,
  getOccupancyHistory: PropTypes.func.isRequired,
  occupancyHistoryPending: PropTypes.bool,
  locationList: PropTypes.array.isRequired,
  occupancyHistoryPage: PropTypes.number,
  setOccupancyHistoryPage: PropTypes.func,
  occupancyHistoryFilters: PropTypes.object,
  setOccupancyHistoryFilters: PropTypes.func,
};

export default OccupancyHistoryTab;
